import BoxInfo from '@/components/boxInfo';
import ButtonActions from '@/components/breadcrumb/components/buttonActions';
import DataGrid from '@/components/dataGrid/dataGrid';
import { PROCEDURE_TARGET_EDITION } from '@/modules/copay/entities/enums/procedures';
import { LABEL_PROCEDURE_TYPE } from '@/modules/copay/entities/enums/usageStatement';
import {
  ProcedureToUpdate,
  UsageStatementProcess,
} from '@/modules/copay/entities/interfaces/usageStatements';
import { useCopayPermissions } from '@/modules/copay/hooks/useCopayPemissions/useCopayPermissions';
import { useProcedure } from '@/modules/copay/hooks/useProcedures/useProcedures';
import { LabelProcedureService } from '@/modules/copay/services/labelProcedureService';
import { getCountProceduresMessage, getFieldMissingMessage } from '@/modules/copay/utils/messages';
import { transformFormProceduresToPayload } from '@/modules/copay/utils/transformFormProceduresToPayload';
import { useRequestPaginatedList } from '@/shared/hooks/useRequestData/useRequestPaginatedList';
import ICONS from '@/shared/entities/constants/icons';
import { groupBy, keyBy, startCase } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface ProceduresLabelProps {
  usageStatementProcess?: UsageStatementProcess;
  boxLoading?: boolean;
  buttonLoading?: boolean;
  callbackSuccess?: () => void;
}

export const ProceduresLabel: React.FC<ProceduresLabelProps> = ({
  usageStatementProcess,
  boxLoading,
  buttonLoading,
  callbackSuccess,
}) => {
  const { canAct } = useCopayPermissions({
    usageStatementProcess,
    target: PROCEDURE_TARGET_EDITION.LABEL,
  });

  const proceduresWithEssencialFields: ProcedureToUpdate[] = useMemo(() => {
    return (
      usageStatementProcess?.issueProcedures?.map((procedure) =>
        usageStatementProcess.labelAddedAt
          ? procedure
          : {
              ...procedure,
              labelProcedureMainId: null,
              labelProcedureSubId: null,
              isPackage: null,
              isHomecare: null,
              isGroupTaxes: null,
            },
      ) || []
    );
  }, [usageStatementProcess?.issueProcedures, usageStatementProcess?.labelAddedAt]);

  const [formProcedures, setFormProcedures] = useState(
    keyBy<Omit<ProcedureToUpdate, 'description'>>(proceduresWithEssencialFields, 'id'),
  );

  const fieldsToUpdate: (keyof ProcedureToUpdate)[] = [
    'labelProcedureMainId',
    'labelProcedureSubId',
    'isPackage',
    'isHomecare',
    'isGroupTaxes',
  ];
  const { fieldMissingMessage, countFieldMissing } = getFieldMissingMessage({
    fieldsByProcedureId: formProcedures,
    usageStatementProcess,
    fieldsToCount: fieldsToUpdate,
  });

  const { data: labelProcedures = [], loading: loadingLabelProcedures } = useRequestPaginatedList({
    autoLoad: true,
    source: () => new LabelProcedureService().getLabelProcedures(),
  });

  const { updateMany, loading: updating } = useProcedure({
    callbackSuccess,
  });

  const labelProceduresByType = useMemo(() => {
    const groupGyType = groupBy(labelProcedures, (labelProcedure) => labelProcedure.type);

    return {
      main: groupGyType.main || [],
      sub: groupGyType.sub || [],
    };
  }, [labelProcedures]);

  const updateProcedure = useCallback((procedureId: number, key: string, value: unknown) => {
    setFormProcedures((current) => ({
      ...current,
      [procedureId]: {
        ...current[procedureId],
        [key]: value,
      },
    }));
  }, []);

  const selectBoolean = useCallback(
    (procedureId: number, key: string, value: unknown) => {
      return (
        <ButtonActions
          label={typeof value == 'boolean' ? String(Boolean(value)).toUpperCase() : ''}
          width={70}
          withLayer={100}
          disabled={!canAct}
          actionOptions={[
            {
              label: 'TRUE',
              onClick: () => {
                updateProcedure(procedureId, key, true);
              },
            },
            {
              label: 'FALSE',
              onClick: () => {
                updateProcedure(procedureId, key, false);
              },
            },
          ]}
        />
      );
    },
    [canAct, updateProcedure],
  );

  const selectLabel = useCallback(
    (
      procedureId: number,
      key: string,
      type: LABEL_PROCEDURE_TYPE,
      labelProcedureId: string | null,
    ) => {
      const label = startCase(
        labelProceduresByType[type].find((labelProcedure) => labelProcedure.id === labelProcedureId)
          ?.name,
      );

      return (
        <ButtonActions
          width={150}
          withLayer={350}
          wrapLabel
          disabled={!canAct}
          label={label}
          invertColor
          actionOptions={labelProceduresByType[type].map((labelProcedure) => ({
            label: startCase(labelProcedure.name),
            onClick: () => {
              updateProcedure(procedureId, key, labelProcedure.id);
            },
          }))}
        />
      );
    },
    [canAct, labelProceduresByType, updateProcedure],
  );

  return (
    <BoxInfo
      title='Rótulos pendentes'
      icon={ICONS.EDIT}
      loading={boxLoading || loadingLabelProcedures}
      header={
        !usageStatementProcess?.labelAddedAt && usageStatementProcess?.issueProcedures?.length ? (
          <Text
            size='medium'
            value={getCountProceduresMessage(usageStatementProcess, 'rótulo')}
            icon={ICONS.INFO}
            iconColor='danger'
            color='danger'
            applyBaseColor
            rounded
            padding='8px'
          />
        ) : null
      }
      footer={
        canAct && (
          <Grid alignContent='right' alignItems='center' gap={8}>
            <Text value={fieldMissingMessage} />

            <Button
              testId='copay-procedures-label-submit'
              label='Enviar'
              disabled={!canAct || countFieldMissing > 0}
              onClick={() =>
                updateMany(
                  transformFormProceduresToPayload({
                    updates: formProcedures,
                    fieldsToPayload: [...fieldsToUpdate, 'id'],
                  }),
                )
              }
              loading={updating || buttonLoading}
            />
          </Grid>
        )
      }>
      <Grid column gap={16}>
        <DataGrid
          elevation={0}
          source={() => ({
            data: usageStatementProcess?.issueProcedures || [],
          })}
          columns={[
            {
              headerName: 'Código',
              field: 'id',
            },
            {
              headerName: 'Adicionar descrição',
              field: 'description',
            },
            {
              headerName: 'Novo Rótulo (N1)',
              render: ({ id }) =>
                selectLabel(
                  id,
                  'labelProcedureMainId',
                  LABEL_PROCEDURE_TYPE.MAIN,
                  formProcedures[id].labelProcedureMainId,
                ),
            },
            {
              headerName: 'Novo Rótulo (N2)',
              render: ({ id }) =>
                selectLabel(
                  id,
                  'labelProcedureSubId',
                  LABEL_PROCEDURE_TYPE.SUB,
                  formProcedures[id].labelProcedureSubId,
                ),
            },
            {
              headerName: 'Flag Pacote',
              render: ({ id }) => selectBoolean(id, 'isPackage', formProcedures[id].isPackage),
            },
            {
              headerName: 'Flag HomeCare',
              render: ({ id }) => selectBoolean(id, 'isHomecare', formProcedures[id].isHomecare),
            },
            {
              headerName: 'AGRUPA TAXA?',
              render: ({ id }) =>
                selectBoolean(id, 'isGroupTaxes', formProcedures[id].isGroupTaxes),
            },
          ]}
        />
      </Grid>
    </BoxInfo>
  );
};
